import React, { useState, useEffect } from 'react';
import { Activity, ActivityEditorProps } from '../types';
import { ActivityInfo } from '../ActivityCommon';
import ActivityHeaderEditor from './ActivityHeaderEditor';
import toast from 'react-hot-toast';

// Assignment Editor Component
const AssignmentEditor: React.FC<ActivityEditorProps> = ({ activity, onActivityUpdate }) => {
  const [instructions, setInstructions] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [title, setTitle] = useState(activity.title);
  const [description, setDescription] = useState(activity.description || '');

  // Initialize fields from activity when component mounts or activity changes
  useEffect(() => {
    if (activity.instructions) {
      setInstructions(activity.instructions);
    }
    if (activity.due_date) {
      setDueDate(activity.due_date);
    }
    setTitle(activity.title);
    setDescription(activity.description || '');
  }, [activity]);

  // Handle AI generated content from parent component
  useEffect(() => {
    const handleAiContent = (event: CustomEvent) => {
      if (event.detail && event.detail.activityId === activity.activity_id) {
        handleAiGeneratedContent(event.detail.content);
      }
    };
    
    window.addEventListener('ai-content-generated' as any, handleAiContent as EventListener);
    
    return () => {
      window.removeEventListener('ai-content-generated' as any, handleAiContent as EventListener);
    };
  }, [activity.activity_id]);

  // Handle AI generated content
  const handleAiGeneratedContent = (generatedContent: any) => {
    console.log('AI generated assignment content:', generatedContent);
    
    if (generatedContent.title) {
      setTitle(generatedContent.title);
    }
    
    if (generatedContent.description) {
      setDescription(generatedContent.description);
    }
    
    if (generatedContent.instructions) {
      setInstructions(generatedContent.instructions);
    }
    
    if (generatedContent.due_date) {
      setDueDate(generatedContent.due_date);
    } else if (generatedContent.raw_response) {
      toast.error('AI generated content could not be automatically applied. Please try again with a more specific prompt.');
    }
  };

  const handleSaveAssignment = async () => {
    if (!onActivityUpdate) return;
    
    try {
      setIsSaving(true);
      
      // Prepare updated assignment data
      const updatedActivity = {
        ...activity,
        instructions: instructions,
        due_date: dueDate,
        title: title,
        description: description
      };
      
      // Save via the provided update handler
      await onActivityUpdate(updatedActivity);
      
      toast.success('Assignment saved successfully!');
    } catch (error) {
      console.error('Error saving assignment:', error);
      toast.error('Error saving assignment. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Activity Header */}
      <ActivityHeaderEditor
        title={title}
        description={description}
        activityType="Assignment"
        onTitleChange={setTitle}
        onDescriptionChange={setDescription}
        titlePlaceholder="Assignment Title"
        descriptionPlaceholder="Assignment Description"
      />
      <div className="mt-4">
        <h4 className="text-md font-medium mb-2">Assignment Details</h4>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">Instructions</label>
            <textarea 
              className="w-full h-24 border border-gray-300 rounded-md p-2 mt-1" 
              placeholder="Enter assignment instructions..."
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Due Date</label>
            <input 
              type="date" 
              className="mt-1 block w-full border border-gray-300 rounded-md p-2"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </div>
        </div>
      </div>
      
      <div className="flex justify-end mt-4">
        <button
          onClick={handleSaveAssignment}
          className="px-4 py-2 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Save Assignment'}
        </button>
      </div>
    </div>
  );
};

export default AssignmentEditor; 